import { useState } from 'react';
import { Button, Container, Divider, Group, NumberInput, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { COMMISSION_RETENTION_RATE, calculateCommissionSummary, formatCurrencyCL } from '../utils/commissionCalculator';

const INITIAL_VALUES = { serviceTotal: '', commissionPercent: '', advances: '', withdrawals: '' };
const MONEY_INPUT_PROPS = {
  min: 0,
  max: Number.MAX_SAFE_INTEGER,
  allowNegative: false,
  allowDecimal: false,
  prefix: '$',
  thousandSeparator: '.',
  decimalSeparator: ',',
  inputMode: 'numeric',
  placeholder: '$0',
};

export default function Calculadora() {
  const [values, setValues] = useState(INITIAL_VALUES);
  const summary = calculateCommissionSummary(values);
  const updateValue = (field) => (value) => setValues((current) => ({ ...current, [field]: value }));
  const rows = [
    ['Comisión bruta', summary.grossCommission],
    [`Descuento de boleta (${new Intl.NumberFormat('es-CL').format(COMMISSION_RETENTION_RATE * 100)}%)`, -summary.retention],
    ['Comisión neta', summary.netCommission],
    ['Adelantos', -Number(values.advances || 0)],
    ['Retiros de productos', -Number(values.withdrawals || 0)],
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap="xl">
        <Stack gap="xs">
          <Title order={1} fz={{ base: 26, sm: 34 }}>Calculadora de comisiones</Title>
          <Text c="dimmed">Consulta rápida con montos manuales. No guarda datos ni registra movimientos.</Text>
        </Stack>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Paper p={{ base: 'md', sm: 'xl' }} withBorder>
            <Stack gap="md">
              <Title order={2} fz="lg">Datos del cálculo</Title>
              <NumberInput {...MONEY_INPUT_PROPS} label="Total de servicios" description="Monto total producido en servicios" value={values.serviceTotal} onChange={updateValue('serviceTotal')} />
              <NumberInput label="Porcentaje de comisión" placeholder="0%" min={0} max={100} clampBehavior="strict" allowNegative={false} decimalScale={2} decimalSeparator="," inputMode="decimal" suffix="%" value={values.commissionPercent} onChange={updateValue('commissionPercent')} />
              <NumberInput {...MONEY_INPUT_PROPS} label="Adelantos" description="Monto a descontar; déjalo vacío si no corresponde" value={values.advances} onChange={updateValue('advances')} />
              <NumberInput {...MONEY_INPUT_PROPS} label="Retiros de productos" description="Valor total de los productos a descontar" value={values.withdrawals} onChange={updateValue('withdrawals')} />
              <Button variant="default" leftSection={<IconRefresh size={18} />} onClick={() => setValues(INITIAL_VALUES)}>Limpiar cálculo</Button>
            </Stack>
          </Paper>
          <Paper p={{ base: 'md', sm: 'xl' }} withBorder>
            <Stack gap="lg">
              <Title order={2} fz="lg">Resultado</Title>
              <Stack gap="md">
                {rows.map(([label, amount]) => (
                  <Group key={label} justify="space-between" gap="xs">
                    <Text size="sm">{label}</Text>
                    <Text fw={600} c={amount < 0 ? 'red' : undefined}>{formatCurrencyCL(amount)}</Text>
                  </Group>
                ))}
              </Stack>
              <Divider />
              <Group justify="space-between" gap="xs">
                <Text size="sm">Total adelantos y retiros</Text>
                <Text fw={600}>{formatCurrencyCL(summary.totalDeductions)}</Text>
              </Group>
              <Paper p="md" bg={summary.finalTotal < 0 ? 'red.0' : 'indigo.0'} shadow="none" role="status" aria-live="polite" aria-atomic="true">
                <Text fw={600}>{summary.finalTotal < 0 ? 'Saldo pendiente por cubrir' : 'Total a recibir'}</Text>
                <Text fz={{ base: 28, sm: 36 }} fw={800} c={summary.finalTotal < 0 ? 'red.8' : 'indigo.8'} style={{ overflowWrap: 'anywhere' }}>{formatCurrencyCL(summary.finalTotal)}</Text>
              </Paper>
              <Text size="sm" c="dimmed">Se aplica el porcentaje al total de servicios, se descuenta la boleta y luego los adelantos y retiros de productos.</Text>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
